import { useEffect, useContext } from "react";
import { API_Version, ApiVersionInfo, ResultObj, allFetch, getControllerApiVersion } from "../Domain";
import { ApiContext, useApi } from "../apiProvider";
import { DateTimeFormate, defaultRangeDateToDotNet6Format } from "../function";

/**產生apiManager實例，並透過factory概念，將產生好的東西放到Provider，往後要用就是從Provider拿出來用
 * @returns 
 */
export const useFilesApi = () => {
    const apiManagerName = "FilesApiManager";
    const context = useContext(ApiContext);
    if (!!!context)
        throw new Error("FilesApiManager must be used within a ApiProvider");

    let { apiManagers, registerApiManager } = context;

    useEffect(() => {
        if (!apiManagers[apiManagerName]) {
            const newUserCareerRankApiManager = new FilesApiManager();
            newUserCareerRankApiManager.init().then((apiManager) => {
                registerApiManager(apiManagerName, apiManager);
            }).catch((e) => {
                console.error("ESD Error!", e);
            })
        }
    }, [apiManagers[apiManagerName]]);

    return useApi<FilesApiManager>(apiManagerName);
};

/** 實際上判斷現在是哪個apiManager的地方 */
export class FilesApiManager implements FilesApiInterface {
    private adapter: FilesDotNet6Adapter | FilesFrameWorkAdapter | null = null;
    private version: keyof typeof API_Version | undefined;
    public isReady: boolean = false;

    async init(): Promise<FilesApiManager> {
        let target_version: string | undefined = this.version;
        if (target_version === undefined) {
            // 沒有的話就嘗試取得api_version.json，取得不到就採用localstorage的預設API版本
            let getAPIversionResult: ApiVersionInfo | null = await getControllerApiVersion("Files");
            target_version = getAPIversionResult === null ? this.version : getAPIversionResult.version;
        }

        if (target_version === "dotNet6") this.adapter = new FilesDotNet6Adapter();
        else if (target_version === 'dotNet6_AM_N') this.adapter = new FilesDotNet6Adapter();//預設是 dotNet6，有需要才再另寫Adapter
        else if (target_version === "framework") this.adapter = new FilesFrameWorkAdapter();

        if (this.adapter) {
            this.SearchFile = this.adapter.SearchFile;
            this.UploadFile = this.adapter.UploadFile;
            this.DeleteFile = this.adapter.DeleteFile;
            this.MoveFile = this.adapter.MoveFile;
            this.UploadHtmlPDF = this.adapter.UploadHtmlPDF;
            this.UploadHtmlPDFNoWait = this.adapter.UploadHtmlPDFNoWait;
            this.isReady = true;

        }

        return this;
    }

    emptyAdapter = async (): Promise<ResultObj> => ({ success: false, message: '遺失 FilesAdapter，請先執行init()，初始化 FilesAdapter', data: null });

    SearchFile = async (obj: SearchFileParams): Promise<ResultObj<SearchFileResItem[]>> => await this.emptyAdapter();
    UploadFile = async (obj: UploadFileParams): Promise<ResultObj<UploadFileResItem[]>> => await this.emptyAdapter();
    DeleteFile = async (obj: DeleteFileParams): Promise<ResultObj> => await this.emptyAdapter();
    MoveFile = async (obj: MoveFileParams): Promise<ResultObj> => await this.emptyAdapter();
    UploadHtmlPDF = async (obj: UploadHtmlPDFParams): Promise<ResultObj> => await this.emptyAdapter();
    UploadHtmlPDFNoWait = async (obj: UploadHtmlPDFNoWaitParams): Promise<ResultObj> => await this.emptyAdapter();

}

/** 定義Meal controller的adapter需要有哪些function */
export interface FilesApiInterface {
    /** @description 檔案查詢 */
    SearchFile: (obj: SearchFileParams) => Promise<ResultObj<SearchFileResItem[]>>;
    /** @description 檔案上傳 */
    UploadFile: (obj: UploadFileParams) => Promise<ResultObj<UploadFileResItem[]>>;
    /** @description 檔案刪除 */
    DeleteFile: (obj: DeleteFileParams) => Promise<ResultObj>;
    /** @description 檔案移動 */
    MoveFile: (obj: MoveFileParams) => Promise<ResultObj>;
    /** @description 從html轉pdf儲存 */
    UploadHtmlPDF: (obj: UploadHtmlPDFParams) => Promise<ResultObj>;
    /** @description 從html轉pdf儲存(不須檔案路徑回傳) */
    UploadHtmlPDFNoWait: (obj: UploadHtmlPDFNoWaitParams) => Promise<ResultObj>;
}

class FilesDotNet6Adapter implements FilesApiInterface {
    SearchFile = async (obj: SearchFileParams): Promise<ResultObj<SearchFileResItem[]>> =>
        await allFetch('Files/SearchFile', {
            ...obj,
            // 不能直接送 Date 物件，JSON.stringify 時會自動轉 UTC 導致日期提前一天
            cre_time_start: defaultRangeDateToDotNet6Format(obj.cre_time_start, "start"),
            cre_time_end: defaultRangeDateToDotNet6Format(obj.cre_time_end, "end"),
        }).then((res: ResultObj<SearchFileDotNet6ResItem[]>) => ({
            success: res.success,
            message: res.message,
            page: res.page,
            data: Object.getPrototypeOf(res.data).constructor.name === "Array" && res.data.length > 0
                ? res.data.map((item: SearchFileDotNet6ResItem) => ({
                    ...item,
                    cre_time: item.cre_time
                        ? DateTimeFormate.dateFormate({ dateStr: item.cre_time, Mode: DateTimeFormate.DateMode.yyyyMMddhhmmss })
                        : '',
                    upd_time: item.upd_time
                        ? DateTimeFormate.dateFormate({ dateStr: item.upd_time, Mode: DateTimeFormate.DateMode.yyyyMMddhhmmss })
                        : '',
                }))
                : res.data as any
        }))

    UploadFile = async (obj: UploadFileParams): Promise<ResultObj<UploadFileResItem[]>> => {
        let formData = new FormData();
        if (obj.file) formData.append(obj.file.name, obj.file);
        formData.append("UploadFile", JSON.stringify(obj.UploadFile));
        return await allFetch<FormData>('Files/UploadFile', formData);
    }

    DeleteFile = async (obj: DeleteFileParams): Promise<ResultObj> =>
        await allFetch('Files/DeleteFile', obj);

    MoveFile = async (obj: MoveFileParams): Promise<ResultObj> =>
        await allFetch('Files/MoveFile', {
            ...obj,
            type: obj.type ?? null
        });

    UploadHtmlPDF = async (obj: UploadHtmlPDFParams): Promise<ResultObj> =>
        await allFetch('Files/UploadHtmlPDF', obj);

    UploadHtmlPDFNoWait = async (obj: UploadHtmlPDFNoWaitParams): Promise<ResultObj> =>
        await allFetch('Files/UploadHtmlPDFNoWait', obj);
}

class FilesFrameWorkAdapter implements FilesApiInterface {
    SearchFile = async (obj: SearchFileParams): Promise<ResultObj<SearchFileResItem[]>> =>
        await allFetch('Files/SearchFile', obj);

    UploadFile = async (obj: UploadFileParams): Promise<ResultObj<UploadFileResItem[]>> => {
        let formData = new FormData();
        if (obj.file) formData.append(obj.file.name, obj.file);
        formData.append("UploadFile", JSON.stringify(obj.UploadFile));
        return await allFetch<FormData>('Files/UploadFile', formData);
    }

    DeleteFile = async (obj: DeleteFileParams): Promise<ResultObj> =>
        await allFetch('Files/DeleteFile', obj);

    MoveFile = async (obj: MoveFileParams): Promise<ResultObj> =>
        await allFetch('Files/MoveFile', obj);

    UploadHtmlPDF = async (obj: UploadHtmlPDFParams): Promise<ResultObj> =>
        await allFetch('Files/UploadHtmlPDF', obj);

    UploadHtmlPDFNoWait = async (obj: UploadHtmlPDFNoWaitParams): Promise<ResultObj> =>
        await allFetch('Files/UploadHtmlPDFNoWait', obj);
}

/** 檔案查詢 (參數)
 * @description 檔案查詢 (參數)
 * @param {string} file_id 檔案流水號
 * @param {string} belong 檔案所屬
 * @param {string} type 檔案類型
 * @param {string} id 所屬流水號
 * @param {string} cre_time_start 建立時間起
 * @param {string} cre_time_end 建立時間迄
 * @param {string} blob 是否blob
 * @param {string} isvalid 是否有效 
 * 
 * */
export interface SearchFileParams {
    file_id?: string,
    belong?: string,
    type?: string,
    id?: string,
    cre_time_start?: string,
    cre_time_end?: string,
    blob?: boolean,
    isvalid?: string,
    ds_id?: string,
}

/** @description 檔案查詢 (回傳)*/
export interface SearchFileResItem {
    belong: string;
    cre_time: string;
    file_id: number;
    filename: string;
    id: string;
    isvalid: string;
    path: string;
    type: string;
}

/** @description DotNet6 檔案查詢 (回傳)*/
export interface SearchFileDotNet6ResItem {
    cre_userid: string;
    cre_time: string | null;
    upd_userid: string;
    upd_time: string | null;
    file_id: number;
    file_code: string;
    belong: string;
    id: string;
    type: string;
    path: string;
    isvalid: string;
    custom_key1: string;
    custom_key2: string;
    filename: string;
    custom_name1: string;
    custom_name2: string;
    blob_result: {
        version: {
            major: number;
            minor: number;
            build: number;
            revision: number;
            majorRevision: number;
            minorRevision: number;
        };
        content: {
            headers: {
                key: string;
                value: string[];
            }[];
        };
        statusCode: number;
        reasonPhrase: string;
        headers: {
            key: string;
            value: string[];
        }[];
        trailingHeaders: {
            key: string;
            value: string[];
        }[];
        requestMessage: {
            version: {
                major: number;
                minor: number;
                build: number;
                revision: number;
                majorRevision: number;
                minorRevision: number;
            };
            versionPolicy: number;
            content: {
                headers: {
                    key: string;
                    value: string[];
                }[];
            };
            method: {
                method: string;
            };
            requestUri: string;
            headers: {
                key: string;
                value: string[];
            }[];
            options: {
                additionalProp1: string;
                additionalProp2: string;
                additionalProp3: string;
            };
        };
        isSuccessStatusCode: true;
    };
}

/** @description 檔案查詢 (回傳)*/
export interface UploadFileResItem {
    file_id: number;
    path: string;
    upload_key: string;
}

/** 檔案上傳 (參數) 
 * @description 檔案上傳 (參數) 
 * @param {string} belong 檔案所屬
 * @param {string} type 檔案類型
 * @param {string} id 所屬流水號
 * @param {string} base64string 圖片Base64字串
 *  */
export interface UploadFileParams {
    file?: File,
    UploadFile: UploadFileJSON
}

export type UploadFileJSON = {
    belong: string;
    id: string;
    type: string;
    base64string?: string;
    is_remain_filename?: boolean;
}

/** 檔案刪除 (參數) 
 * @description 檔案刪除 (參數) 
 * @param {string} file_id 檔案流水號
 *  */
export interface DeleteFileParams {
    file_id: number;
}

/** 檔案移動 (參數) 
 * @description 檔案移動 (參數) 
 * @param {string} belong 檔案所屬
 * @param {string} type 檔案類型
 * @param {string} file_id 檔案流水號
 * @param {string} id 新所屬流水號
 *  */
export interface MoveFileParams {
    belong: string;
    file_id: number;
    id: string;
    type?: string;
}

/** 從html轉pdf儲存 (參數) 
 * @description 從html轉pdf儲存 (參數) 
 * @param {string} belong 檔案所屬
 * @param {string} type 檔案類型
 * @param {string} id 新所屬流水號
 * @param {string} pdf_html 要轉pdf的html
 * @param {string} header_html html的header
 * @param {string} header_height html的header的高度(inch)
 * @param {string} footer_html html的footer
 *  */
export interface UploadHtmlPDFParams {
    file_code?: string;
    belong: string;
    type: string;
    id: string;
    base64string?: string;
    custom_key1?: string;
    custom_key2?: string;
    pdf_html?: string;
    header_html?: string;
    header_height?: number;
    footer_html?: string;
    company_code?: string;
}

/** 從html轉pdf儲存(不須檔案路徑回傳) (參數)
 * @description 從html轉pdf儲存(不須檔案路徑回傳) (參數) 
 * @param {string} belong 檔案所屬
 * @param {string} type 檔案類型
 * @param {string} id 新所屬流水號
 * @param {string} pdf_html 要轉pdf的html
 * @param {string} header_html html的header
 * @param {string} header_height html的header的高度(inch)
 * @param {string} footer_html html的footer
 *  */
export interface UploadHtmlPDFNoWaitParams {
    file_code?: string;
    belong: string;
    type: string;
    id: string;
    base64string?: string;
    custom_key1?: string;
    custom_key2?: string;
    pdf_html: string;
    header_html?: string;
    header_height?: number;
    footer_html?: string;
    company_code?: string;
}
